import { Col, message, notification, Popconfirm, Row, Select, Space, Spin, Table } from 'antd'
import {
  Button,
  ButtonActionBar,
  DeleteAndUpdate,
  DropdownMenu,
  EmptyScreen,
  InputCell,
} from 'modules/core/components'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import emptyStateImage from 'assets/images/compositions/empty-big.svg'
import configuration from 'modules/configuration'
import { NotificationRecipientCreateModal } from './components'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { generateMessageError, generateSelectOptions } from 'modules/core/utils'
import { usePagination } from 'modules/core/customHooks'
import { client } from 'httpclient'

const NotificationRecipientsList = ({
  fetchNotificationRecipients,
  notificationRecipientsList,
  deleteNotificationRecipient,
  editNotificationRecipient,
  fetchAllDimensions,
  fetchAllDimensionsValues,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editRow, setEditRow] = useState(null)
  const [dataCellEdit, setDataCellEdit] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [tablePagination, setTablePagination] = usePagination()
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()

  const [documentTypes, setDocumentTypes] = useState([])
  const [allDimensionsValuesCompany, setAllDimensionsValuesCompany] = useState([])
  const [allDimensionsValuesCeco, setAllDimensionsValuesCeco] = useState([])

  useEffect(() => {
    setIsLoading(true)
    fetchNotificationRecipients({ ...tablePagination }).then(() => setIsLoading(false))
  }, [fetchNotificationRecipients, tablePagination])

  useEffect(() => {
    client.get('/cnr-approvals/document-types/').then((response) => {
      const data = response.data
      const items = Array.isArray(data) ? data : data?.results ?? data?.data ?? []
      setDocumentTypes(items)
    })
  }, [])

  useEffect(() => {
    fetchAllDimensions().then((response) => {
      const dimensions = response.payload.data
      const idCompania = dimensions.find((el) => el.name === 'sociedad')?.id
      const idCeco = dimensions.find((el) => el.name === 'ceco')?.id

      if (idCompania) {
        fetchAllDimensionsValues({ id_dimension: idCompania }).then((res) => {
          setAllDimensionsValuesCompany(res.payload.data)
        })
      }
      if (idCeco) {
        fetchAllDimensionsValues({ id_dimension: idCeco }).then((res) => {
          setAllDimensionsValuesCeco(res.payload.data)
        })
      }
    })
  }, [fetchAllDimensions, fetchAllDimensionsValues])

  const dataSource = useMemo(
    () =>
      Array.isArray(notificationRecipientsList)
        ? notificationRecipientsList
        : notificationRecipientsList?.results || [],
    [notificationRecipientsList],
  )

  const documentTypeOptions = useMemo(
    () => generateSelectOptions({ options: Array.isArray(documentTypes) ? documentTypes : [] }),
    [documentTypes],
  )

  const companyOptions = useMemo(
    () =>
      generateSelectOptions({
        options: allDimensionsValuesCompany.map((dim) => ({
          ...dim,
          id: dim.code_destination,
          name: dim.name_destination,
          code: dim.code_destination,
        })),
      }),
    [allDimensionsValuesCompany],
  )

  const cecoOptions = useMemo(
    () =>
      generateSelectOptions({
        options: allDimensionsValuesCeco.map((dim) => ({
          ...dim,
          id: dim.code_destination,
          name: dim.name_destination,
          code: dim.code_destination,
        })),
      }),
    [allDimensionsValuesCeco],
  )

  const documentTypesMap = useMemo(() => {
    const map = {}
    documentTypes.forEach((d) => {
      map[d.id] = d.code ? `${d.code} - ${d.name}` : d.name
    })
    return map
  }, [documentTypes])

  const companyMap = useMemo(() => {
    const map = {}
    allDimensionsValuesCompany.forEach((d) => {
      map[d.code_destination] = d.code_destination
        ? `${d.code_destination} - ${d.name_destination}`
        : d.name_destination
    })
    return map
  }, [allDimensionsValuesCompany])

  const cecoMap = useMemo(() => {
    const map = {}
    allDimensionsValuesCeco.forEach((d) => {
      map[d.code_destination] = d.code_destination
        ? `${d.code_destination} - ${d.name_destination}`
        : d.name_destination
    })
    return map
  }, [allDimensionsValuesCeco])

  const onCellEdit = (name, value) => {
    setDataCellEdit({ ...dataCellEdit, [`${name}`]: value })
  }

  const onConfirm = () => {
    setEditRow(null)
    setDataCellEdit({})
  }

  const onCancel = () => setEditRow(null)

  const handleDelete = () => {
    setIsSaving(true)
    deleteNotificationRecipient(deleteId)
      .then(() =>
        fetchNotificationRecipients({ ...tablePagination }).then(() => {
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
          setIsSaving(false)
          setDeleteId(null)
        }),
      )
      .catch((error) => {
        setIsSaving(false)
        setDeleteId(null)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const handleEdit = (id) => {
    setIsSaving(true)
    const original = dataSource.find((item) => item.id === id)
    const { id: _id, ...rest } = original
    const payload = { ...rest, ...dataCellEdit }
    editNotificationRecipient(id, payload)
      .then(() =>
        fetchNotificationRecipients({ ...tablePagination }).then(() => {
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
          setIsSaving(false)
          setDataCellEdit({})
          setEditRow(null)
        }),
      )
      .catch((error) => {
        setIsSaving(false)
        setDataCellEdit({})
        setEditRow(null)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const columns = [
    {
      dataIndex: 'document_type',
      title: t('FIELD_DOCUMENT_TYPE'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return (
            <Select
              defaultValue={value}
              options={documentTypeOptions}
              onChange={(val) => onCellEdit('document_type', val)}
              style={{ width: '100%' }}
              placeholder={t('ACTION_SELECT')}
            />
          )
        }
        return documentTypesMap[value] || value
      },
    },
    {
      dataIndex: 'company_code',
      title: t('FIELD_COMPANY_CODE'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return (
            <Select
              defaultValue={value}
              options={companyOptions}
              onChange={(val) => onCellEdit('company_code', val)}
              style={{ width: '100%' }}
              placeholder={t('ACTION_SELECT')}
            />
          )
        }
        return companyMap[value] || value
      },
    },
    {
      dataIndex: 'cost_center_code',
      title: t('FIELD_COST_CENTER_CODE'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return (
            <Select
              defaultValue={value}
              options={cecoOptions}
              onChange={(val) => onCellEdit('cost_center_code', val)}
              style={{ width: '100%' }}
              placeholder={t('ACTION_SELECT')}
            />
          )
        }
        return cecoMap[value] || value
      },
    },
    {
      dataIndex: 'email',
      title: t('FIELD_EMAIL'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return <InputCell initialValue={value} name="email" onCellUpdate={onCellEdit} />
        }
        return value
      },
    },
    {
      dataIndex: 'id',
      title: t('FIELD_ACTIONS'),
      align: 'right',
      render: (id) => {
        return id !== editRow ? (
          <Popconfirm
            placement="bottomRight"
            title={t('CONFIG_NOTIFICATION_RECIPIENT_DELETE_CONFIRMATION')}
            okText={t('ACTION_DELETE')}
            onConfirm={() => handleDelete()}
            okButtonProps={{ loading: isSaving }}
            cancelButtonProps={{ disabled: isSaving }}
            cancelText={t('ACTION_CANCEL')}
            onCancel={() => setDeleteId(null)}
            visible={deleteId === id}
          >
            <Space size="middle">
              <DropdownMenu
                title={t('ACTION_MORE')}
                menu={[
                  {
                    title: t('ACTION_EDIT'),
                    icon: <EditOutlined />,
                    onClick: () => setEditRow(id),
                  },
                  {
                    title: t('ACTION_DELETE'),
                    icon: <DeleteOutlined />,
                    onClick: () => setDeleteId(id),
                  },
                ]}
              />
            </Space>
          </Popconfirm>
        ) : (
          <DeleteAndUpdate
            onCancel={onCancel}
            onConfirm={onConfirm}
            onSave={() => handleEdit(id)}
            dataCellEdit={dataCellEdit}
            loading={isSaving}
          />
        )
      },
    },
  ]

  return (
    <Spin spinning={isLoading}>
      {dataSource.length === 0 && !isLoading && (
        <EmptyScreen
          image={emptyStateImage}
          description={t('CONFIG_NOTIFICATION_RECIPIENT_EMPTY_DESCRIPTION')}
          footer={
            <Button.Primary title="ACTION_CREATE_NEW" onClick={() => setShowCreateModal(true)} />
          }
        />
      )}
      {dataSource.length > 0 && (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <ButtonActionBar>
              <Button.Primary title="ACTION_CREATE_NEW" onClick={() => setShowCreateModal(true)} />
            </ButtonActionBar>
          </Col>
          <Col span={24}>
            <Table
              size="small"
              columns={columns}
              dataSource={dataSource}
              rowKey="id"
              loading={isSaving}
              onChange={setTablePagination}
              pagination={{
                pageSize: tablePagination.page_size,
                current: tablePagination.page,
                total: notificationRecipientsList?.count ?? dataSource.length,
                size: 'small',
                pageSizeOptions: ['10', '20', '30'],
                showSizeChanger: true,
              }}
            />
          </Col>
        </Row>
      )}
      <NotificationRecipientCreateModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onRefetch={() => fetchNotificationRecipients({ ...tablePagination })}
        documentTypes={documentTypes}
        allDimensionsValuesCompany={allDimensionsValuesCompany}
        allDimensionsValuesCeco={allDimensionsValuesCeco}
      />
    </Spin>
  )
}

const mapStateToProps = (state) => ({
  notificationRecipientsList: configuration.selectors.getNotificationRecipientsList(state),
})

const mapDispatchToProps = {
  fetchNotificationRecipients: configuration.actions.fetchNotificationRecipients,
  deleteNotificationRecipient: configuration.actions.deleteNotificationRecipient,
  editNotificationRecipient: configuration.actions.editNotificationRecipient,
  fetchAllDimensions: configuration.actions.fetchAllDimensions,
  fetchAllDimensionsValues: configuration.actions.fetchAllDimensionsValues,
}

export default connect(mapStateToProps, mapDispatchToProps)(NotificationRecipientsList)
